<template>
  <div class="actions-manager-container">
    <div class="actions-sidebar-panel">
      <div class="actions-sidebar-header">
        <h2>Actions Planifiées</h2>
        <button class="add-action-btn" @click="createNewAction">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Nouvelle Action
        </button>
      </div>
      <div class="actions-list">
        <div 
          v-for="action in actions" 
          :key="action._id" 
          class="action-item"
          :class="{ 'is-active': selectedAction && selectedAction._id === action._id }"
          @click="selectAction(action)"
        >
          <div class="action-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <div class="action-info">
            <span class="action-name">{{ action.name || 'Action sans nom' }}</span>
            <span class="action-trigger">{{ getTriggerLabel(action.trigger_type) }}</span>
          </div>
          <button class="delete-action-btn" @click.stop="deleteAction(action)" title="Supprimer">
             <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12"></path>
             </svg>
          </button>
        </div>
        <div v-if="actions.length === 0" class="empty-actions">
           Aucune action configurée.
        </div>
      </div>
    </div>
    
    <div class="action-editor" v-if="selectedAction">
      <div class="editor-body">
        <div class="action-title-section">
          <input type="text" v-model="selectedAction.name" placeholder="Nom de l'action..." class="action-name-input-large" />
          <button class="save-action-btn" @click="saveAction">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            Enregistrer
          </button>
        </div>
        <!-- 1. Déclencheur -->
        <div class="action-section">
          <div class="section-badge">1</div>
          <div class="element-card" style="flex: 1;">
            <div class="element-header">
              <span class="element-type-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span>Déclencheur</span>
              </span>
            </div>
            <div class="element-content">
              <p class="section-desc">Quand cette action doit-elle s'exécuter ?</p>

              <!-- Choix du type de déclencheur via cartes -->
              <div class="trigger-cards">
                <div class="trigger-card" :class="{ active: selectedAction.trigger_type === 'manual' }" @click="setTriggerType('manual')">
                  <div class="trigger-card-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                    </svg>
                  </div>
                  <div class="trigger-card-text">
                    <span class="trigger-card-title">Immédiat</span>
                    <span class="trigger-card-desc">Au passage dans le bloc</span>
                  </div>
                </div>

                <div class="trigger-card" :class="{ active: selectedAction.trigger_type === 'cron' }" @click="setTriggerType('cron')">
                  <div class="trigger-card-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                  </div>
                  <div class="trigger-card-text">
                    <span class="trigger-card-title">Programmé</span>
                    <span class="trigger-card-desc">Après un délai ou en cycle</span>
                  </div>
                </div>

                <div class="trigger-card" :class="{ active: selectedAction.trigger_type === 'event' }" @click="setTriggerType('event')">
                  <div class="trigger-card-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"></path>
                      <polyline points="7 9 12 4 17 9"></polyline>
                      <line x1="12" y1="4" x2="12" y2="16"></line>
                    </svg>
                  </div>
                  <div class="trigger-card-text">
                    <span class="trigger-card-title">Sur variable</span>
                    <span class="trigger-card-desc">Quand une variable change</span>
                  </div>
                </div>
              </div>

              <!-- PROGRAMMÉ : une seule fois (délai) ou récurrent (cycle) -->
              <div v-if="selectedAction.trigger_type === 'cron'" class="trigger-panel">
                <div class="mode-pills">
                  <button type="button" class="mode-pill" :class="{ active: selectedAction.schedule_mode === 'once' }" @click="setScheduleMode('once')">
                    Une seule fois
                  </button>
                  <button type="button" class="mode-pill" :class="{ active: selectedAction.schedule_mode === 'recurring' }" @click="setScheduleMode('recurring')">
                    Récurrent
                  </button>
                </div>

                <div class="interval-row">
                  <span class="interval-prefix">{{ selectedAction.schedule_mode === 'once' ? 'Exécuter dans' : 'Toutes les' }}</span>
                  <input type="number" min="1" v-model.number="selectedAction.interval_value" class="modern-input interval-input" />
                  <select v-model="selectedAction.interval_unit" class="modern-select interval-unit">
                    <option value="minutes">minutes</option>
                    <option value="hours">heures</option>
                    <option value="days">jours</option>
                  </select>
                  <span v-if="selectedAction.schedule_mode === 'once'" class="interval-suffix">après le passage</span>
                </div>

                <small class="helper-text" v-if="selectedAction.schedule_mode === 'once'">
                  ⏱️ L'action s'exécutera <strong>une seule fois</strong>, {{ selectedAction.interval_value }} {{ selectedAction.interval_unit }} après que l'utilisateur passe sur la carte (ex. relance d'abandon).
                </small>
                <small class="helper-text" v-else>
                  🔁 L'action s'exécutera <strong>à répétition</strong>, toutes les {{ selectedAction.interval_value }} {{ selectedAction.interval_unit }}, tant que les conditions sont remplies.
                </small>
              </div>

              <!-- SUR VARIABLE : variable surveillée -->
              <div v-if="selectedAction.trigger_type === 'event'" class="trigger-panel">
                 <label>Variable à surveiller</label>
                 <VariableSelector
                   v-model="selectedAction.watched_variable"
                   :variables="variables"
                   :allow-custom="true"
                   placeholder="Variable à surveiller"
                 />
                 <label style="margin-top: 14px;">Se déclenche quand la valeur devient</label>
                 <div class="event-trigger-row">
                   <select v-model="selectedAction.trigger_operator" class="modern-select event-op">
                     <option v-for="op in operators" :key="op.value" :value="op.value">{{ op.symbol }}  {{ op.label }}</option>
                   </select>
                   <input type="text" v-model="selectedAction.trigger_value" placeholder="Valeur déclencheuse" class="modern-input" style="flex: 1;" />
                 </div>
              </div>

              <!-- Gestion des conflits (ré-amorçage pour le même utilisateur) -->
              <div class="input-group" style="margin-top: 20px;">
                 <label>Si l'action est ré-amorcée alors qu'une instance est déjà active pour cet utilisateur</label>
                 <select v-model="selectedAction.conflict_resolution" class="modern-select">
                    <option value="ignore">Ignorer — garder l'instance existante (évite les doublons)</option>
                    <option value="overwrite">Réinitialiser — supprimer l'ancienne puis recréer</option>
                    <option value="parallel">Créer une instance en parallèle</option>
                 </select>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 2. Conditions -->
        <div class="action-section">
          <div class="section-badge">2</div>
          <div class="element-card" style="flex: 1;">
            <div class="element-header">
              <span class="element-type-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="16 18 22 12 16 6"></polyline>
                  <polyline points="8 6 2 12 8 18"></polyline>
                </svg>
                <span>Conditions (Optionnel)</span>
              </span>
            </div>
            <div class="element-content">
              <p class="section-desc">Vérifier des variables avant d'exécuter l'action</p>
              <div class="conditions-list">
                 <div v-for="(cond, index) in selectedAction.conditions" :key="index" style="width: 100%;">
                   <div class="condition-row">
                      <div class="input-wrapper variable-wrapper js-variable-select" style="position: relative; width: 100%; flex: 1;">
                        <VariableSelector 
                          v-model="cond.variable"
                          :variables="variables"
                          :allow-custom="true"
                          placeholder="Variable"
                        />
                      </div>
                      
                      <div class="input-wrapper operator-wrapper" style="flex-shrink: 0; width: 70px;">
                        <div 
                          class="custom-select" 
                          :class="{ active: activeDropdown === index }"
                          @click="toggleDropdown(index, $event)"
                        >
                          <div class="selected-option">
                            <span class="operator-symbol">{{ getOperatorSymbol(cond.operator) }}</span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="chevron-icon">
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </div>
                          
                          <div class="options-dropdown" v-if="activeDropdown === index">
                            <div 
                              v-for="op in operators" 
                              :key="op.value"
                              class="option-item"
                              :class="{ selected: cond.operator === op.value }"
                              @click.stop="selectOperator(cond, op.value)"
                            >
                              <span class="option-symbol">{{ op.symbol }}</span>
                              <span class="option-label">{{ op.label }}</span>
                              <svg v-if="cond.operator === op.value" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="check-icon">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>

                      <input type="text" v-model="cond.value" placeholder="Valeur" class="modern-input short" style="flex: 1;" />
                      <button class="remove-cond-btn" @click="selectedAction.conditions.splice(index, 1)" title="Retirer">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                   </div>
                   
                   <!-- Logic Gate Between Rows -->
                   <div 
                     v-if="index < selectedAction.conditions.length - 1" 
                     class="logic-gate-separator"
                   >
                     <div class="gate-line"></div>
                     <div class="gate-options small">
                       <label :class="{ active: selectedAction.logic_gate === 'AND' }">
                         <input type="radio" value="AND" v-model="selectedAction.logic_gate">
                         ET
                       </label>
                       <label :class="{ active: selectedAction.logic_gate === 'OR' }">
                         <input type="radio" value="OR" v-model="selectedAction.logic_gate">
                         OU
                       </label>
                     </div>
                     <div class="gate-line"></div>
                   </div>
                 </div>
                 <button class="add-cond-btn" @click="addCondition">
                   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                     <line x1="12" y1="5" x2="12" y2="19"></line>
                     <line x1="5" y1="12" x2="19" y2="12"></line>
                   </svg>
                   Ajouter une condition
                 </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 3. Exécution -->
        <div class="action-section">
          <div class="section-badge">3</div>
          <div class="element-card" style="flex: 1;">
            <div class="element-header">
              <span class="element-type-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                </svg>
                <span>Exécution</span>
              </span>
            </div>
            <div class="element-content">
              <p class="section-desc">Que doit faire le chatbot ?</p>
              <div class="input-group">
                <select v-model="selectedAction.execution_type" class="modern-select" @change="onExecutionTypeChange">
                  <option value="send_block" :disabled="isLongDelay">
                    Déclencher un bloc{{ isLongDelay ? ' — indisponible après 24h' : '' }}
                  </option>
                  <option value="send_template">Envoyer un template WhatsApp{{ isLongDelay ? ' (recommandé)' : '' }}</option>
                  <option value="set_variable">Modifier une variable</option>
                </select>
              </div>

              <!-- Fenêtre 24h WhatsApp : le bloc est désactivé, on guide vers les autres actions -->
              <div v-if="isLongDelay" class="window-warning">
                <div class="window-warning-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </div>
                <div class="window-warning-body">
                  <strong>Délai ≥ 24h — hors fenêtre WhatsApp</strong>
                  <p>Au-delà de 24h sans message de l'utilisateur, WhatsApp <strong>ne délivre pas</strong> un bloc libre (risque de blocage / spam). « Déclencher un bloc » est donc <strong>indisponible</strong> ici — choisis un <strong>Template WhatsApp</strong> approuvé ou une autre action.</p>
                </div>
              </div>

              <div v-if="selectedAction.execution_type === 'send_block'" class="input-group">
                 <label>Bloc à déclencher</label>
                 <div class="block-autocomplete-wrapper" style="position: relative; width: 100%;">
                   <BlockSelector
                     v-model="selectedAction.execution_target"
                     :customBlocks="blocks"
                     :idBot="chatbotId"
                     placeholder="Sélectionner un bloc..."
                   />
                 </div>
              </div>

              <!-- SEND_TEMPLATE : info de base (nom + langue). Le backend gère l'envoi à l'exécution. -->
              <div v-if="selectedAction.execution_type === 'send_template' && selectedAction.template" class="input-group">
                 <label>Nom du template</label>
                 <select v-if="templates.length" v-model="selectedAction.template.name" class="modern-select" @change="onActionTemplateChange">
                   <option value="">— Sélectionner un template —</option>
                   <option v-for="tpl in templates" :key="tpl.name" :value="tpl.name">
                     {{ tpl.name }} ({{ tpl.language }})
                   </option>
                 </select>
                 <input v-else type="text" v-model="selectedAction.template.name" class="modern-input" placeholder="Nom exact du template" />

                 <label style="margin-top: 12px;">Langue</label>
                 <input type="text" v-model="selectedAction.template.language" class="modern-input tpl-lang" placeholder="fr" />

                 <!-- Paramètres du corps : valeur fixe OU variable (remplacée à l'envoi) -->
                 <div class="template-params" v-if="selectedAction.template.name">
                   <div class="tpl-params-head">
                     <label>Paramètres du corps</label>
                     <button type="button" class="tpl-add-param" @click="addManualParam">+ Ajouter un paramètre</button>
                   </div>
                   <div v-for="param in templateParamKeys" :key="param" class="template-param-row">
                     <span class="param-key">{{ paramLabel(param) }}</span>
                     <input
                       type="text"
                       class="modern-input"
                       :value="rawParamValue(param)"
                       @input="setRawParamValue(param, $event.target.value)"
                       placeholder="Valeur fixe ou variable (via + Variable)"
                     />
                     <select class="modern-select param-var-select" @change="insertParamVariable(param, $event.target.value); $event.target.value = ''">
                       <option value="">+ Variable</option>
                       <option v-for="v in variables" :key="v.id" :value="v.id">{{ v.slug || v.id }}</option>
                     </select>
                   </div>
                   <small v-if="!templateParamKeys.length" class="helper-text">Aucun paramètre détecté dans ce template. Tu peux en ajouter manuellement si besoin.</small>
                   <small v-else class="helper-text">💡 Tape une valeur fixe (identique pour tous), ou ajoute une variable via « + Variable » (remplacée à l'envoi pour chaque destinataire).</small>
                 </div>

                 <small class="helper-text" style="margin-top: 10px;">Le backend enverra ce template au destinataire au moment de l'exécution.</small>
              </div>

              <!-- SET_VARIABLE : à implémenter (UI minimale) -->
              <div v-if="selectedAction.execution_type === 'set_variable'" class="input-group">
                 <small class="helper-text">⚙️ « Modifier une variable » sera configurable prochainement.</small>
              </div>
            </div>
          </div>
        </div>
        
      </div>
    </div>
    
    <div class="action-editor-empty" v-else>
      <div class="empty-icon-wrapper">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
        </svg>
      </div>
      <h3>Sélectionnez ou créez une Action</h3>
      <p>Les actions permettent de programmer des tâches en arrière-plan (relances, rappels, vérifications) sans passer par des webhooks externes.</p>
    </div>
  </div>
</template>

<script>
import VariableSelector from '@/components/common/VariableSelector.vue';
import BlockSelector from '@/components/common/BlockSelector.vue';
import axios from 'axios';
import { actionsApiBaseUrl, apiBaseUrl } from '@/config';
import { consoleError, consoleInfo } from '@/utils/logger';

export default {
  name: 'ActionsManager',
  components: {
    VariableSelector,
    BlockSelector
  },
  props: {
    chatbotId: {
      type: String,
      required: true
    },
    chatbot: {
      type: Object,
      default: () => ({})
    },
    blocks: {
      type: Array,
      default: () => []
    },
    variables: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      actions: [],
      selectedAction: null,
      activeDropdown: null,
      templates: [],
      templatesLoading: false,
      operators: [
        { value: '==', label: 'Egal à', symbol: '=' },
        { value: '!=', label: 'Différent de', symbol: '≠' },
        { value: '>', label: 'Supérieur à', symbol: '>' },
        { value: '<', label: 'Inférieur à', symbol: '<' },
        { value: '>=', label: 'Sup. ou égal', symbol: '≥' },
        { value: '<=', label: 'Inf. ou égal', symbol: '≤' },
        { value: 'contains', label: 'Contient', symbol: '⊂' },
        { value: 'in', label: 'Est dans', symbol: '∈' }
      ]
    }
  },
  mounted() {
    this.fetchActions();
    if (this.chatbotSender) this.fetchActionTemplates();
    document.addEventListener('click', this.closeDropdowns);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeDropdowns);
  },
  computed: {
    // Durée planifiée convertie en heures (0 si non applicable)
    scheduledHours() {
      const a = this.selectedAction;
      if (!a || a.trigger_type !== 'cron') return 0;
      const v = parseInt(a.interval_value) || 0;
      const u = a.interval_unit || 'hours';
      if (u === 'minutes') return v / 60;
      if (u === 'days') return v * 24;
      return v; // hours
    },
    // Vrai si l'action programmée tombe au-delà de la fenêtre 24h WhatsApp
    isLongDelay() {
      return this.scheduledHours >= 24;
    },
    // Numéro expéditeur du bot (pour charger les templates)
    chatbotSender() {
      return this.chatbot && (this.chatbot.display_phone_number || this.chatbot.from_display_phone_number) || '';
    },
    // Liste des paramètres ({{1}}, {{2}}…) détectés dans le corps du template sélectionné
    actionTemplateParams() {
      const a = this.selectedAction;
      if (!a || !a.template || !a.template.name) return [];
      const tpl = this.templates.find(t => t.name === a.template.name);
      if (!tpl || !tpl.components) return [];
      const body = tpl.components.find(c => c.type === 'BODY');
      if (!body || !body.text) return [];
      const matches = body.text.match(/\{\{([^}]+)\}\}/g) || [];
      return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '').trim()))];
    },
    // Union des paramètres détectés (corps du template) et de ceux déjà saisis (manuels)
    templateParamKeys() {
      const a = this.selectedAction;
      const manual = (a && a.template && a.template.parameters) ? Object.keys(a.template.parameters) : [];
      return [...new Set([...this.actionTemplateParams, ...manual])];
    }
  },
  watch: {
    // Au-delà de 24h, "Déclencher un bloc" n'est plus permis -> on bascule vers Template
    isLongDelay(val) {
      if (val) this.enforceExecutionConstraints();
    },
    // Le numéro du bot peut arriver de façon asynchrone -> on charge les templates dès qu'il est dispo
    chatbotSender(val) {
      if (val && !this.templates.length && !this.templatesLoading) {
        this.fetchActionTemplates();
      }
    }
  },
  methods: {
    getTriggerLabel(type) {
      const map = {
        manual: 'Immédiat',
        cron: 'Programmé',
        event: 'Sur variable'
      };
      return map[type] || 'Inconnu';
    },
    createNewAction() {
      const newAction = {
        _id: 'temp_' + Date.now(),
        name: 'Nouvelle Action',
        trigger_type: 'manual',
        conflict_resolution: 'ignore',
        schedule_mode: 'once',
        interval_value: 12,
        interval_unit: 'hours',
        watched_variable: '',
        trigger_operator: '==',
        trigger_value: '',
        logic_gate: 'AND',
        conditions: [],
        execution_type: 'send_block',
        execution_target: ''
      };
      this.actions.push(newAction);
      this.selectedAction = newAction;
      this.ensureScheduleDefaults();
    },
    selectAction(act) {
      this.selectedAction = act;
      this.ensureScheduleDefaults();
    },
    async fetchActions() {
      try {
        const response = await axios.get(`${actionsApiBaseUrl}/api/actions/${this.chatbotId}`);
        if (response.data && response.data.success) {
          this.actions = response.data.actions || [];
          if (this.actions.length > 0) {
            this.selectedAction = this.actions[0];
            this.ensureScheduleDefaults();
          }
        }
      } catch (error) {
        consoleError('Erreur lors du chargement des actions:', error);
      }
    },
    async deleteAction(act) {
      if (!act._id || act._id.startsWith('temp_')) {
        this.actions = this.actions.filter(a => a._id !== act._id);
        if (this.selectedAction && this.selectedAction._id === act._id) {
          this.selectedAction = null;
        }
        return;
      }
      
      try {
        const response = await axios.delete(`${actionsApiBaseUrl}/api/actions/${act._id}`);
        if (response.data && response.data.success) {
          this.actions = this.actions.filter(a => a._id !== act._id);
          if (this.selectedAction && this.selectedAction._id === act._id) {
            this.selectedAction = null;
          }
        }
      } catch (error) {
        consoleError('Erreur lors de la suppression de l\'action:', error);
      }
    },
    addCondition() {
      if (!this.selectedAction.conditions) this.selectedAction.conditions = [];
      this.selectedAction.conditions.push({ variable: '', operator: '==', value: '' });
    },
    // Garantit que les champs de planification existent (actions anciennes ou nouvelles)
    ensureScheduleDefaults() {
      const a = this.selectedAction;
      if (!a) return;
      if (a.schedule_mode === undefined) a.schedule_mode = 'once';
      if (a.interval_value === undefined || a.interval_value === null) a.interval_value = 12;
      if (!a.interval_unit) a.interval_unit = 'hours';
      if (!a.trigger_operator) a.trigger_operator = '==';
      if (a.watched_variable === undefined) a.watched_variable = '';
      if (a.trigger_value === undefined) a.trigger_value = '';
      this.ensureTemplateObject();
      this.enforceExecutionConstraints();
    },
    // Au-delà de 24h le bloc n'est pas délivrable -> on retire ce choix (bascule vers Template)
    enforceExecutionConstraints() {
      const a = this.selectedAction;
      if (a && this.isLongDelay && a.execution_type === 'send_block') {
        a.execution_type = 'send_template';
      }
    },
    // ─── Templates WhatsApp (execution_type = send_template) ───
    ensureTemplateObject() {
      const a = this.selectedAction;
      if (!a) return;
      if (!a.template || typeof a.template !== 'object') {
        a.template = { name: '', language: '', status: '', templateId: '', category: '', parameters: {} };
      }
      if (!a.template.parameters) a.template.parameters = {};
    },
    onExecutionTypeChange() {
      if (this.selectedAction && this.selectedAction.execution_type === 'send_template') {
        this.ensureTemplateObject();
        if (!this.templates.length) this.fetchActionTemplates();
      }
    },
    async fetchActionTemplates() {
      if (!this.chatbotSender) return;
      this.templatesLoading = true;
      try {
        const response = await axios.get(`${apiBaseUrl}/template/list/${this.chatbotSender}`);
        if (response.data && response.data.success && response.data.data && response.data.data.data) {
          this.templates = response.data.data.data;
        }
      } catch (e) {
        consoleError('Erreur chargement templates:', e);
      } finally {
        this.templatesLoading = false;
      }
    },
    onActionTemplateChange() {
      this.ensureTemplateObject();
      const a = this.selectedAction;
      const tpl = this.templates.find(t => t.name === a.template.name);
      if (tpl) {
        a.template.language = tpl.language || '';
        a.template.status = tpl.status || '';
        a.template.templateId = tpl.id || '';
        a.template.category = (tpl.category || '').toLowerCase();
      }
      a.template.parameters = {}; // reset des paramètres au changement de template
    },
    paramLabel(param) {
      return '{' + '{' + param + '}' + '}';
    },
    getActionParamValue(param) {
      const a = this.selectedAction;
      const val = (a && a.template && a.template.parameters) ? a.template.parameters[param] : '';
      if (!val) return '';
      const match = String(val).match(/^\{\{(.+)\}\}$/);
      return match ? match[1] : val;
    },
    setActionParamValue(param, val) {
      this.ensureTemplateObject();
      const a = this.selectedAction;
      if (!val) { a.template.parameters[param] = ''; return; }
      const isVar = this.variables.some(v => v.id === val);
      a.template.parameters[param] = isVar ? `{{${val}}}` : val;
    },
    // Valeur brute du paramètre (texte fixe ou "{{variable}}") — éditable directement
    rawParamValue(param) {
      const a = this.selectedAction;
      return (a && a.template && a.template.parameters && a.template.parameters[param]) || '';
    },
    setRawParamValue(param, val) {
      this.ensureTemplateObject();
      this.selectedAction.template.parameters[param] = val;
    },
    // Insère un jeton {{variable}} dans la valeur du paramètre (à la suite du texte existant)
    insertParamVariable(param, varId) {
      if (!varId) return;
      this.ensureTemplateObject();
      const current = this.selectedAction.template.parameters[param] || '';
      this.selectedAction.template.parameters[param] = current + `{{${varId}}}`;
    },
    // Ajoute un paramètre manuel (clé numérique suivante : "1", "2", …)
    addManualParam() {
      this.ensureTemplateObject();
      const params = this.selectedAction.template.parameters;
      const next = String(this.templateParamKeys.length + 1);
      if (params[next] === undefined) params[next] = '';
    },
    // Sélection du type de déclencheur via les cartes
    setTriggerType(type) {
      if (!this.selectedAction) return;
      this.selectedAction.trigger_type = type;
      if (type === 'cron') this.ensureScheduleDefaults();
    },
    // Sélection du mode de planification (une fois / récurrent)
    setScheduleMode(mode) {
      if (!this.selectedAction) return;
      this.selectedAction.schedule_mode = mode;
    },
    async saveAction() {
      if (!this.selectedAction) return;
      
      try {
        if (this.selectedAction._id && !this.selectedAction._id.startsWith('temp_')) {
          // UPDATE
          const response = await axios.put(`${actionsApiBaseUrl}/api/actions/${this.selectedAction._id}`, this.selectedAction);
          if (response.data && response.data.success) {
            consoleInfo('Action mise à jour avec succès');
            // update in list
            const index = this.actions.findIndex(a => a._id === this.selectedAction._id);
            if (index !== -1) {
              this.actions[index] = { ...this.selectedAction };
            }
          }
        } else {
          // CREATE
          const payload = { ...this.selectedAction };
          delete payload._id; // Remove temp ID
          const response = await axios.post(`${actionsApiBaseUrl}/api/actions/${this.chatbotId}`, payload);
          if (response.data && response.data.success && response.data.action) {
            consoleInfo('Action créée avec succès');
            // Replace temp action with the saved one (which has the real _id)
            const index = this.actions.findIndex(a => a._id === this.selectedAction._id);
            if (index !== -1) {
              this.actions[index] = response.data.action;
              this.selectedAction = this.actions[index];
            }
          }
        }
      } catch (error) {
        consoleError('Erreur lors de la sauvegarde de l\'action:', error);
      }
    },
    toggleDropdown(index, event) {
      event.stopPropagation();
      this.activeDropdown = this.activeDropdown === index ? null : index;
    },
    selectOperator(cond, operatorValue) {
      cond.operator = operatorValue;
      this.activeDropdown = null;
    },
    closeDropdowns() {
      this.activeDropdown = null;
    },
    getOperatorSymbol(value) {
        const op = this.operators.find(o => o.value === value);
        return op ? op.symbol : value;
    }
  }
}
</script>

<style scoped>
.actions-manager-container {
  display: flex;
  width: 100%;
  height: 100%;
  background: #ffffff;
  overflow: hidden;
}

.actions-sidebar-panel {
  width: 320px;
  background: #ffffff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
}

.actions-sidebar-header {
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.actions-sidebar-header h2 {
  margin: 0;
  font-size: 1.2rem;
  color: #1e293b;
  font-weight: 600;
}

.add-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #DAFFD1; /* Peelo Light */
  color: #143028; /* Peelo Dark */
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 10px rgba(218, 255, 209, 0.2);
}

.add-action-btn:hover {
  background: #c2eab8; /* Hover state slightly darker */
  transform: translateY(-1px);
  box-shadow: 0 6px 15px rgba(218, 255, 209, 0.3);
}

.actions-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s;
}

.action-item:hover {
  background: #f1f5f9;
}

.action-item.is-active {
  background: #143028; /* Peelo dark */
  border-color: #143028;
  box-shadow: 0 4px 15px rgba(20, 48, 40, 0.15);
}

.action-icon {
  color: #64748b;
  display: flex;
}

.action-item.is-active .action-icon {
  color: #DAFFD1; /* Peelo light */
}

.action-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.action-name {
  font-weight: 600;
  color: #334155;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.action-item.is-active .action-name {
  color: #ffffff;
}

.action-trigger {
  font-size: 0.8rem;
  color: #64748b;
}

.action-item.is-active .action-trigger {
  color: #cbd5e1;
}

.delete-action-btn {
  background: transparent;
  border: none;
  color: #cbd5e1;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  opacity: 0;
  transition: all 0.2s;
}

.action-item.is-active .delete-action-btn {
  color: #94a3b8;
}

.action-item:hover .delete-action-btn {
  opacity: 1;
}

.delete-action-btn:hover {
  color: #ef4444;
  background: #fee2e2;
}

.action-item.is-active .delete-action-btn:hover {
  color: #ef4444;
  background: rgba(254, 226, 226, 0.15);
}

.empty-actions {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  font-size: 0.9rem;
}

/* Editor Styles */
.action-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  overflow-y: auto;
}

.editor-header {
  padding: 20px 40px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
}

.editor-body {
  padding: 80px 40px 40px 40px; /* Top padding to ensure it clears the fixed playground-action-bar */
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

.action-title-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  background: #ffffff;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.action-name-input-large {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  margin-right: 20px;
}

.action-name-input-large::placeholder {
  color: #cbd5e1;
}

.save-action-btn {
  background: #143028;
  color: #daffd1;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.save-action-btn:hover {
  background: #1a4035;
  transform: translateY(-1px);
}

.action-section {
  display: flex;
  gap: 24px;
  margin-bottom: 40px;
  position: relative;
}

/* Ensure earlier sections stack above later ones so dropdowns aren't clipped */
.action-section:nth-of-type(1) { z-index: 10; }
.action-section:nth-of-type(2) { z-index: 9; }
.action-section:nth-of-type(3) { z-index: 8; }
.action-section:nth-of-type(4) { z-index: 7; }
.action-section:nth-of-type(5) { z-index: 6; }

.action-section::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 40px;
  bottom: -40px;
  width: 0;
  border-left: 3px dotted #cbd5e1;
  z-index: 0;
  opacity: 0.7;
}

.action-section:last-child::before {
  display: none;
}

.section-badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #daffd1;
  color: #143028;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  flex-shrink: 0;
  z-index: 1;
  border: 4px solid #ffffff;
}

.element-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.element-card:hover {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.element-header {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  background: #143028;
  border-bottom: 1px solid rgba(218, 255, 209, 0.1);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #daffd1;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.element-content {
  padding: 1.5rem;
}

.section-desc {
  margin: 0 0 20px 0;
  color: #64748b;
  font-size: 0.95rem;
}

.input-group {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-group label {
  font-weight: 600;
  color: #475569;
  font-size: 0.9rem;
}

.modern-input, .modern-select {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #334155;
  background: #f8fafc;
  outline: none;
  transition: all 0.2s;
  font-family: inherit;
}

.modern-input:focus, .modern-select:focus {
  border-color: #143028;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(20, 48, 40, 0.1);
}

/* ───────── Trigger type cards ───────── */
.trigger-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 4px;
}

.trigger-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.trigger-card:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
  transform: translateY(-1px);
}

.trigger-card.active {
  border-color: #143028;
  background: #f3fbf0;
  box-shadow: 0 4px 14px rgba(20, 48, 40, 0.12);
}

.trigger-card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #64748b;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.trigger-card.active .trigger-card-icon {
  background: #143028;
  color: #DAFFD1;
}

.trigger-card-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.trigger-card-title {
  font-weight: 700;
  color: #1e293b;
  font-size: 0.95rem;
}

.trigger-card-desc {
  font-size: 0.78rem;
  color: #64748b;
  line-height: 1.2;
  margin-top: 2px;
}

/* ───────── Panneau de réglage du déclencheur ───────── */
.trigger-panel {
  margin-top: 16px;
  padding: 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: triggerPanelIn 0.2s ease-out;
}

@keyframes triggerPanelIn {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

.trigger-panel > label {
  font-weight: 600;
  color: #475569;
  font-size: 0.9rem;
}

/* ───────── Mode pills (une fois / récurrent) ───────── */
.mode-pills {
  display: inline-flex;
  background: #e8edf2;
  padding: 4px;
  border-radius: 10px;
  gap: 4px;
  align-self: flex-start;
}

.mode-pill {
  border: none;
  background: transparent;
  padding: 8px 18px;
  border-radius: 7px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.18s ease;
}

.mode-pill:hover {
  color: #143028;
}

.mode-pill.active {
  background: #ffffff;
  color: #143028;
  box-shadow: 0 2px 6px rgba(20, 48, 40, 0.12);
}

/* ───────── Sélecteur d'intervalle ───────── */
.interval-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.interval-prefix,
.interval-suffix {
  font-size: 0.9rem;
  color: #475569;
  font-weight: 600;
  white-space: nowrap;
}

.interval-input {
  width: 80px;
  flex-shrink: 0;
  text-align: center;
  font-weight: 700;
  color: #143028;
}

.interval-unit {
  width: 130px;
  flex-shrink: 0;
}

.helper-text strong {
  color: #143028;
}

/* ───────── Event trigger row ───────── */
.event-trigger-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.event-op {
  width: 160px;
  flex-shrink: 0;
}

/* ───────── Sélecteur de template ───────── */
.template-config {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background: #ffffff;
}

.tpl-sender-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #475569;
}

.tpl-sender-line strong {
  color: #143028;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.tpl-load-btn {
  background: #143028;
  color: #DAFFD1;
  border: none;
  padding: 7px 14px;
  border-radius: 7px;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.tpl-load-btn:hover:not(:disabled) {
  background: #1a4035;
}

.tpl-load-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.tpl-lang {
  max-width: 160px;
}

.tpl-params-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.tpl-add-param {
  background: transparent;
  border: 1px dashed #cbd5e1;
  color: #475569;
  padding: 5px 12px;
  border-radius: 7px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tpl-add-param:hover {
  border-color: #143028;
  color: #143028;
  background: #f3fbf0;
}

.template-params {
  margin-top: 14px;
  padding: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.template-param-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.param-var-select {
  width: 130px;
  flex-shrink: 0;
  font-size: 0.85rem;
}

.param-key {
  flex-shrink: 0;
  min-width: 48px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-weight: 700;
  color: #143028;
  background: #DAFFD1;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.82rem;
  text-align: center;
}

/* ───────── Avertissement fenêtre 24h WhatsApp ───────── */
.window-warning {
  display: flex;
  gap: 14px;
  margin-top: 14px;
  padding: 16px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-left: 4px solid #f59e0b;
  border-radius: 12px;
}

.window-warning-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: #fef3c7;
  color: #b45309;
  display: flex;
  align-items: center;
  justify-content: center;
}

.window-warning-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.window-warning-body strong {
  color: #92400e;
  font-size: 0.92rem;
}

.window-warning-body p {
  margin: 0;
  color: #78510a;
  font-size: 0.85rem;
  line-height: 1.45;
}

.window-warning-btn {
  align-self: flex-start;
  margin-top: 6px;
  background: #f59e0b;
  color: #ffffff;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.window-warning-btn:hover {
  background: #d97706;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.conditions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.condition-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.short {
  flex: 1;
}

.short-select {
  width: 80px;
  flex-shrink: 0;
}

.remove-cond-btn {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  transition: all 0.2s;
}

.remove-cond-btn:hover {
  opacity: 1;
  background: #fee2e2;
}

.add-cond-btn {
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px dashed #cbd5e1;
  background: transparent;
  color: #64748b;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.add-cond-btn:hover {
  border-color: #fb923c;
  color: #c2410c;
  background: #fff7ed;
}

.action-editor-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 40px;
  text-align: center;
}

.empty-icon-wrapper {
  width: 80px;
  height: 80px;
  background: #e2e8f0;
  color: #64748b;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.action-editor-empty h3 {
  margin: 0 0 12px 0;
  color: #334155;
  font-size: 1.5rem;
}

.action-editor-empty p {
  margin: 0;
  color: #64748b;
  max-width: 400px;
  line-height: 1.5;
}

/* Custom Select Styles */
.custom-select {
  position: relative;
  width: 100%;
  height: 38px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
}

.custom-select.active {
  border-color: #fb923c;
  box-shadow: 0 0 0 2px rgba(251, 146, 60, 0.1);
  background: #ffffff;
}

.selected-option {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 8px;
  gap: 6px;
}

.operator-symbol {
  font-weight: 700;
  color: #ea580c; /* Orange-600 */
  font-size: 1.1rem;
}

.chevron-icon {
  color: #94a3b8;
  transition: transform 0.2s;
}

.custom-select.active .chevron-icon {
  transform: rotate(180deg);
}

.options-dropdown {
  position: absolute;
  top: 110%;
  left: 50%;
  transform: translateX(-50%);
  width: 180px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  z-index: 200;
  max-height: 250px;
  overflow-y: auto;
  animation: fadeIn 0.15s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translate(-50%, -10px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}

.option-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.1s;
  color: #475569;
  font-size: 0.85rem;
}

.option-item:hover {
  background: #fff7ed;
  color: #c2410c;
}

.option-item.selected {
  background: #fff7ed;
  font-weight: 600;
  color: #c2410c;
}

.option-symbol {
  display: inline-block;
  width: 20px;
  font-weight: 700;
  color: #ea580c;
  font-size: 1rem;
  margin-right: 4px;
}

.option-label {
  flex: 1;
}

.check-icon {
  color: #ea580c;
}

/* Logic Gate Separator Styles */
.logic-gate-separator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 0.5rem 0;
  color: #94a3b8;
}

.gate-line {
  height: 1px;
  background: #e2e8f0;
  flex: 1;
}

.gate-options.small {
  background: #f1f5f9;
  padding: 2px;
  border-radius: 6px;
  display: flex;
}

.gate-options.small label {
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 4px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  margin: 0;
}

.gate-options.small label input {
  display: none;
}

.gate-options.small label.active {
  background: white;
  color: #c2410c;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}
</style>
